import { isFailedQueueItemStatus, LatestQueueItem, OptJobType, useLatestQueueItem } from 'api/optJobs/optJobs.api'
import React, { ReactElement, useMemo } from 'react'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import OptimizationFailedGuide from 'ui/molecules/OptimizationFailedGuide/OptimizationFailedGuide'
import Datetime from 'utils/datetime/datetime'

type OptimizationFailedGuideWrapperProps = {
  optJobType: OptJobType
}

function getLatestQueueItem(latestQueueItems: LatestQueueItem[]): LatestQueueItem | null {
  if (latestQueueItems.length === 0) return null

  return latestQueueItems.reduce((latest, current) => {
    return new Date(current.updated_at) > new Date(latest.updated_at) ? current : latest
  })
}

export default function OptimizationFailedGuideWrapper({
  optJobType,
}: OptimizationFailedGuideWrapperProps): ReactElement | null {
  const { activeSystem } = useAuth()

  const { data: latestQueueItems = [] } = useLatestQueueItem({
    systemId: activeSystem?.id,
    type: optJobType,
  })

  const latestQueueItem = useMemo(() => getLatestQueueItem(latestQueueItems), [latestQueueItems])

  const shouldShow = isFailedQueueItemStatus(latestQueueItem?.queue_item_status)

  if (!shouldShow) {
    return null
  }

  return (
    /*
      e2e: durable failure signal for aurora-e2e — do not remove. The chip's own
      copy is localized, and the toast that also reports this is gone after 6s,
      so neither can be selected on. Rendered only while the latest job is in a
      failed state, so its presence IS the signal; data-opt-status names which
      failure it is, because the optimize button reports that in translated prose.
    */
    <div
      data-testid="optimize-failed"
      data-opt-status={latestQueueItem?.queue_item_status}
      className="flex justify-center text-xs border border-warning-4 h-8 rounded items-center font-bold whitespace-nowrap"
    >
      <OptimizationFailedGuide startTime={Datetime.getISONow()} endTime={Datetime.getDateOneWeekAhead()} popup />
    </div>
  )
}
